/**
 * Money display shared by the web (SOW cards, rate tables) and the API (the
 * SOW audit narrator), so an engagement's fee or rate reads the same on the
 * card and in its audit trail.
 */

/** Retainer fees and metric tier prices: whole dollars print without cents
 *  ("$1,550"), anything else with them ("$1,550.25") so a fee is never shown
 *  rounded. Rates always carry cents and use `formatRate`. */
export function formatDollars(n: number): string {
  const digits = Number.isInteger(n) ? 0 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

/**
 * Format a rate as a USD string (`$1,250.00`). Accepts either a number (e.g. a
 * SOW T&M / rate-card rate, already a JS number) or a numeric-string in Postgres
 * `numeric` wire format (e.g. `"125.0000"`). Returns `'—'` for any input
 * that's missing, blank, or not a finite number.
 *
 * The blank-string guard is important: `Number('')` is `0`, which would
 * otherwise render as `"$0.00"` — a silently-invented zero rate. The
 * `Number.isFinite` guard catches `NaN` (non-numeric strings) and
 * `±Infinity` (numbers that overflow JS's double).
 *
 * Used by every view that surfaces a per-hour rate — the SOW T&M line and
 * rate-card displays. Keep them in sync by importing this; do not inline
 * another copy.
 */
export function formatRate(value: string | number | null | undefined): string {
  if (value == null) return '—';
  const trimmed = typeof value === 'string' ? value.trim() : value;
  if (trimmed === '') return '—';
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
